import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Send, X, RotateCcw, User, Clock, Headset, FileText, Search, Bell, BellOff,
  Volume2, VolumeX, Zap, Copy, Crown, AlertTriangle, ChevronDown, Tag, Mail, MessageSquare,
} from "lucide-react";

const STATUS_COLORS = {
  submitted: "bg-sky-100 text-sky-700",
  under_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  paid: "bg-emerald-500 text-white",
  duplicate: "bg-slate-200 text-slate-700",
};

const QUICK_REPLIES = [
  { label: "Greeting", text: "Hey! This is a real human now — how can I help?" },
  { label: "Verifying", text: "Give me a minute while I pull your record up." },
  { label: "Payout ETA", text: "Payouts usually clear within 15–30 minutes once approved. I'll double-check yours." },
  { label: "Need photos", text: "Could you upload a clear photo of the card front and back so we can validate it?" },
  { label: "Escalating", text: "Escalating this to our payments team — I'll follow up here as soon as I hear back." },
  { label: "Thanks + close", text: "Thanks for waiting! Anything else I can help you with today?" },
];

const REF_REGEX = /NEXON-[A-Z0-9]{4,}-[A-Z0-9]{4}/gi;

function fmtRelative(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.round((Date.now() - t) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

/** Renders text with NEXON-XXXX-XXXX auto-linkified. */
function LinkifiedText({ text }) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  const re = /NEXON-[A-Z0-9]{4,}-[A-Z0-9]{4}/gi;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <Link key={`ref-${match.index}`} to={`/admin/submissions?q=${encodeURIComponent(match[0])}`} className="font-mono font-black text-sky-600 hover:text-sky-800 hover:underline">
        {match[0]}
      </Link>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts.map((p, i) => (typeof p === "string" ? <span key={i}>{p}</span> : p))}</>;
}

const FILTERS = [
  { v: "all", label: "All", tone: "text-slate-700" },
  { v: "open", label: "Open", tone: "text-emerald-700" },
  { v: "handoff", label: "Manager needed", tone: "text-rose-700" },
  { v: "closed", label: "Closed", tone: "text-slate-500" },
];

// Business hours: Mon-Sat 9AM-8PM ET
function isBusinessHours() {
  const now = new Date();
  const opts = { timeZone: "America/New_York", hour12: false, weekday: "short", hour: "numeric" };
  const [wk, hr] = now.toLocaleString("en-US", opts).split(", ");
  const hour = parseInt(hr, 10);
  return wk !== "Sun" && hour >= 9 && hour < 20;
}

export default function AdminLiveChat() {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("nexon_chat_sound") !== "off");
  const [quickOpen, setQuickOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);

  // Initialize a soft ping sound (data URI, no asset needed)
  useEffect(() => {
    audioRef.current = new Audio("data:audio/wav;base64,UklGRhwFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YfgEAADl/wYAJgAiADgAKgAoADQAKgAJACYABgD5/9r/y//S/8v/x/++/8P/y/++/9L/6P/i//X/CQD9/xIACwATAAsA+f/y/+j/1v/S/8v/z//G/8P/vf++/8//1v/W/9v/6P/o//L/8v/1//X/9f/1/+j/8v/o/9//4v/S/9L/1v/L/8//x//L/8v/1v/S/9v/4v/o//L/+f8AAAsAJgAoACwALAAsAFEALAA4ACYAJgAiAB4AEwAmAB4AKAA4AC4AKgAmAB4AJgAmACwAKAAsACwAKAAmACIAHgAsACYAKgAmACwAJgAiABMAEwAAABMABgAiABMAJgAeACwAKgAmACwAJgAiAB4AHgAmACYAJgAiADQAJgAsACgAKgAsACYAKAAmACYAHgAsAB4AHgAiABMAEwAeABMAJgAmACwAKgAmACwAJgA4AC4AKgAoACgAKgAiACgAJgAsACgAKgAiAB4AHgAeABMAHgALAAsAEwALAAAA+f8LAAsAEwALABMACwATAAsACwATAAsAEwAeABMAJgAmACwAKgAsACwALAAmACwAJgAmACIAEwAsABMAHgATABMAAAALAAAACwATAAsAEwALABMACwALAAsACwATAAsA+f8TAAsAJgATAAAAEwAAAA==");
    audioRef.current.volume = 0.35;
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q) params.q = q;
      const r = await adminApi.get("/admin/live-chat/sessions", { params });
      setSessions(r.data || []);
      if (!selectedId && (r.data || []).length > 0) setSelectedId(r.data[0].id);

      // Sound + tab-title alert on new managers-requested
      const totalUnread = (r.data || []).reduce((a, s) => a + (s.unread_count || 0), 0);
      const openHandoff = (r.data || []).filter((s) => s.manager_requested && s.status !== "closed").length;
      if (totalUnread > prevUnreadRef.current && soundOn) audioRef.current?.play().catch(() => {});
      prevUnreadRef.current = totalUnread;
      document.title = totalUnread > 0
        ? `(${totalUnread}) GC Nexon Admin`
        : (openHandoff > 0 ? `[!] GC Nexon Admin` : "GC Nexon Admin");
    } catch { /* silent */ }
  }, [filter, q, selectedId, soundOn]);

  useEffect(() => {
    loadSessions();
    const id = setInterval(loadSessions, 5000);
    return () => { clearInterval(id); document.title = "GC Nexon Admin"; };
  }, [loadSessions]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      const r = await adminApi.get(`/admin/live-chat/${selectedId}`);
      setDetail(r.data);
    } catch { /* */ }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return undefined; }
    loadDetail();
    const id = setInterval(loadDetail, 3000);
    return () => clearInterval(id);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || !selectedId) return;
    setSending(true);
    try {
      await adminApi.post(`/admin/live-chat/${selectedId}/message`, { text });
      setDraft("");
      loadDetail();
      loadSessions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };
  const claim = async () => {
    try { await adminApi.post(`/admin/live-chat/${selectedId}/claim`); toast.success("Chat claimed"); loadDetail(); loadSessions(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not claim"); }
  };
  const release = async () => {
    if (!window.confirm("Hand this conversation back to Miles (AI)?")) return;
    try { await adminApi.post(`/admin/live-chat/${selectedId}/release`); toast.success("Released to Miles"); loadDetail(); loadSessions(); }
    catch { toast.error("Could not release"); }
  };
  const close = async () => {
    if (!window.confirm("Close this conversation?")) return;
    try { await adminApi.post(`/admin/live-chat/${selectedId}/close`); toast.success("Closed"); loadDetail(); loadSessions(); }
    catch { toast.error("Could not close"); }
  };
  const copyTranscript = () => {
    const t = (detail?.messages || []).map((m) => {
      const who = m.role === "user" ? (detail.session.customer_name || detail.session.customer_email || "Customer") : (m.role === "agent" ? "Manager" : (m.role === "system" ? "system" : "Miles"));
      return `[${new Date(m.created_at).toLocaleString()}] ${who}: ${m.text}`;
    }).join("\n");
    navigator.clipboard?.writeText(t);
    toast.success("Transcript copied");
  };
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("nexon_chat_sound", next ? "on" : "off");
  };

  // Keyboard shortcuts: ↑/↓ to navigate inbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (!sessions.length) return;
      const idx = sessions.findIndex((s) => s.id === selectedId);
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedId(sessions[Math.min(sessions.length - 1, idx + 1)].id); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelectedId(sessions[Math.max(0, idx - 1)].id); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sessions, selectedId]);

  const session = detail?.session;
  const messages = detail?.messages || [];
  const customerSubs = detail?.submissions || [];

  const counts = useMemo(() => ({
    all: sessions.length,
    open: sessions.filter((s) => s.status !== "closed").length,
    handoff: sessions.filter((s) => s.manager_requested && s.status !== "closed").length,
    closed: sessions.filter((s) => s.status === "closed").length,
  }), [sessions]);

  const insertQuick = (t) => { setDraft((d) => (d ? d + " " + t : t)); setQuickOpen(false); };
  const businessHours = isBusinessHours();

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-4 md:p-6" data-testid="admin-live-chat">

        {/* Header row */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">Live chat</span>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1" data-testid="admin-live-chat-title">Conversations</h1>
            <p className="text-slate-500 mt-1 text-sm flex items-center gap-2">
              Real-time customer conversations · auto handover from Miles
              <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full ${businessHours ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${businessHours ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                {businessHours ? "IN HOURS" : "AFTER HOURS"}
              </span>
            </p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleSound} data-testid="chat-sound-toggle"
              className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg text-xs font-bold transition ${soundOn ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
              {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />} {soundOn ? "Sound on" : "Muted"}
            </button>
          </div>
        </div>

        {/* 3-column console */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr,340px] min-h-[calc(100vh-260px)]">

            {/* INBOX */}
            <aside className="border-r border-slate-100 bg-slate-50/40 flex flex-col" data-testid="chat-inbox">
              <div className="p-3 border-b border-slate-100 space-y-2">
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer name or email…" data-testid="chat-search"
                    className="w-full pl-9 pr-3 h-9 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-indigo-400" />
                </div>
                <div className="flex gap-1 overflow-x-auto no-scrollbar">
                  {FILTERS.map((f) => (
                    <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`chat-filter-${f.v}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-[11px] font-black whitespace-nowrap transition ${
                        filter === f.v ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
                      }`}>
                      {f.label}
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] ${
                        filter === f.v ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                      }`}>{counts[f.v] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto max-h-[calc(100vh-380px)]">
                {sessions.length === 0 && <div className="px-4 py-12 text-center text-xs text-slate-400">No chats match your filter.</div>}
                {sessions.map((s) => {
                  const active = selectedId === s.id;
                  const isClosed = s.status === "closed";
                  return (
                    <button key={s.id} type="button" onClick={() => setSelectedId(s.id)} data-testid={`chat-session-${s.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition ${
                        active ? "bg-white border-l-4 border-l-indigo-500" : "hover:bg-white"
                      } ${isClosed ? "opacity-60" : ""}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {s.vip && <Crown size={11} className="text-amber-500 shrink-0" />}
                          <div className="text-sm font-black text-slate-900 truncate">
                            {s.customer_name || s.customer_email || "Guest visitor"}
                          </div>
                        </div>
                        <div className="text-[10px] text-slate-400 shrink-0">{fmtRelative(s.last_message_at)}</div>
                      </div>
                      <div className="text-xs text-slate-500 truncate leading-snug">
                        {s.last_message_role === "user" ? "" : "↩ "}
                        {s.last_message_preview || "No messages yet"}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        {s.manager_requested && !isClosed && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black bg-rose-500 text-white animate-pulse">
                            NEEDS HUMAN
                          </span>
                        )}
                        {(s.unread_count || 0) > 0 && !isClosed && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black bg-sky-500 text-white">
                            {s.unread_count}
                          </span>
                        )}
                        {s.claimed_by && !isClosed && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black bg-indigo-100 text-indigo-700">HANDLING</span>}
                        {isClosed && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black bg-slate-200 text-slate-600">CLOSED</span>}
                        {s.channel === "telegram" && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black bg-sky-50 text-sky-600 border border-sky-200">TELEGRAM</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-4 py-2 text-[10px] text-slate-400 border-t border-slate-100">
                <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-white font-mono">↑↓</kbd> navigate · updates every 5s
              </div>
            </aside>

            {/* CONVERSATION */}
            <section className="flex flex-col bg-white" data-testid="chat-conversation">
              {!session && (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <MessageSquare size={38} className="text-slate-200" />
                  <div className="text-sm">Select a conversation from the inbox.</div>
                </div>
              )}
              {session && (
                <>
                  {/* Handoff banner */}
                  {session.manager_requested && !session.claimed_by && !session.closed_at && (
                    <div className="px-5 py-3 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={16} />
                        <div>
                          <div className="text-sm font-black">This customer asked for a human</div>
                          <div className="text-[11px] opacity-90">Miles handed off · reason: {session.handoff_reason || "customer requested"}</div>
                        </div>
                      </div>
                      <button onClick={claim} data-testid="chat-claim" className="h-9 px-4 rounded-lg bg-white text-rose-600 text-xs font-black hover:bg-slate-50">
                        Take this chat
                      </button>
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white font-black">
                        {(session.customer_name || session.customer_email || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-black text-slate-900 truncate">{session.customer_name || session.customer_email || "Guest visitor"}</div>
                          {session.vip && (
                            <span title={`VIP · $${session.lifetime_paid} lifetime paid`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] font-black">
                              <Crown size={9} /> VIP
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2">
                          <Clock size={11} /> Started {fmtRelative(session.created_at)}
                          {session.claimed_by && <> · <span className="font-bold text-indigo-600">handled by you</span></>}
                          <span className="text-slate-300">·</span>
                          <span className="font-mono">{session.id.slice(0, 12)}…</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={copyTranscript} data-testid="chat-copy-transcript" title="Copy transcript"
                        className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Copy size={14} /></button>
                      {!session.claimed_by && !session.closed_at && !session.manager_requested && (
                        <button onClick={claim} data-testid="chat-claim-btn" className="h-9 px-3 rounded-lg text-xs font-black bg-gradient-to-r from-indigo-500 to-indigo-700 text-white hover:from-indigo-600 hover:to-indigo-800">
                          Take over
                        </button>
                      )}
                      {session.claimed_by && !session.closed_at && (
                        <>
                          <button onClick={release} data-testid="chat-release" className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-bold border border-slate-200 bg-white hover:bg-slate-50">
                            <RotateCcw className="w-3.5 h-3.5" /> Hand back
                          </button>
                          <button onClick={close} data-testid="chat-close" className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-bold border border-rose-200 bg-white hover:bg-rose-50 text-rose-600">
                            <X className="w-3.5 h-3.5" /> Close
                          </button>
                        </>
                      )}
                      {session.closed_at && <span className="text-xs font-black text-slate-500 px-3 py-1.5 rounded-lg bg-slate-100">CLOSED · {fmtRelative(session.closed_at)}</span>}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/50 max-h-[calc(100vh-460px)]">
                    {messages.map((m) => {
                      const isCustomer = m.role === "user";
                      const isAgent = m.role === "agent";
                      const isSystem = m.role === "system";
                      return (
                        <div key={m.id} className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                            isSystem ? "bg-amber-50 text-amber-800 text-xs border border-amber-200 !max-w-full mx-auto text-center italic"
                              : isCustomer ? "bg-white border border-slate-200 text-slate-900 rounded-tl-none"
                              : isAgent ? "bg-gradient-to-br from-indigo-500 to-indigo-700 text-white rounded-tr-none"
                              : "bg-sky-500 text-white rounded-tr-none"
                          }`}>
                            {!isSystem && !isCustomer && (
                              <div className="text-[10px] font-black opacity-80 mb-0.5 uppercase tracking-wider">
                                {isAgent ? (m.author || "Manager") : "Miles"}
                              </div>
                            )}
                            <LinkifiedText text={m.text || (m.attachments?.length ? "📎 attachment" : "")} />
                            <div className={`text-[9px] mt-1 opacity-70 ${isCustomer ? "text-slate-500" : "text-white/80"}`}>
                              {fmtRelative(m.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {!messages.length && <div className="text-center text-xs text-slate-400 py-16">No messages yet.</div>}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer */}
                  {!session.closed_at && (
                    <div className="border-t border-slate-100 bg-white">
                      {/* Quick replies dropdown */}
                      {quickOpen && (
                        <div className="px-4 py-3 border-b border-slate-100 grid sm:grid-cols-2 gap-2">
                          {QUICK_REPLIES.map((q, i) => (
                            <button key={i} onClick={() => insertQuick(q.text)} className="text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-indigo-50 hover:text-indigo-800 border border-slate-200 text-xs">
                              <div className="font-black text-[10px] uppercase tracking-wider text-indigo-600">{q.label}</div>
                              <div className="text-slate-600 mt-0.5 line-clamp-2">{q.text}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-end gap-2 px-4 py-3">
                        <button onClick={() => setQuickOpen(!quickOpen)} data-testid="chat-quick-replies" title="Quick replies"
                          className={`h-11 px-3 rounded-xl border text-xs font-bold inline-flex items-center gap-1.5 transition ${
                            quickOpen ? "bg-indigo-500 text-white border-indigo-500" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}>
                          <Zap size={13} /> Quick <ChevronDown size={11} className={quickOpen ? "rotate-180 transition" : "transition"} />
                        </button>
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); sendMessage(); } else if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                          placeholder="Type your reply… (Enter to send, Shift+Enter for newline)"
                          rows={2} data-testid="chat-composer"
                          className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400" />
                        <button onClick={sendMessage} disabled={sending || !draft.trim()} data-testid="chat-send"
                          className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl text-sm font-black bg-gradient-to-r from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white shadow disabled:opacity-50">
                          <Send className="w-4 h-4" /> Send
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* CONTEXT */}
            <aside className="border-l border-slate-100 bg-slate-50/40 overflow-y-auto max-h-[calc(100vh-260px)]" data-testid="chat-context">
              {!session && <div className="px-4 py-12 text-center text-xs text-slate-400">Customer context appears here.</div>}
              {session && (
                <div className="px-5 py-4 space-y-6">
                  {/* Customer */}
                  <div>
                    <div className="font-black text-slate-900 flex items-center gap-2 mb-3 text-sm">
                      <User size={14} className="text-slate-500" /> Customer
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <Field label="Name" value={session.customer_name || "(guest)"} />
                      <Field label="Email" value={session.customer_email || "—"} mono />
                      <Field label="Session" value={session.id.slice(0, 12) + "…"} mono />
                      <Field label="Channel" value={session.channel} />
                      {session.lifetime_paid > 0 && (
                        <div className="flex items-baseline gap-2 pt-2">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-black w-16">Lifetime</span>
                          <span className="text-emerald-700 font-black">${session.lifetime_paid.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    {session.customer_email && (
                      <div className="mt-3 flex gap-2">
                        <Link to={`/admin/users?q=${encodeURIComponent(session.customer_email)}`} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-bold hover:bg-slate-50 text-slate-700">
                          <User size={11} /> Account
                        </Link>
                        <a href={`mailto:${session.customer_email}`} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-bold hover:bg-slate-50 text-slate-700">
                          <Mail size={11} /> Email
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {session.tags?.length > 0 && (
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Tag size={11} /> Tags</div>
                      <div className="flex flex-wrap gap-1.5">
                        {session.tags.map((t) => <span key={t} className="inline-flex items-center px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-black">{t}</span>)}
                      </div>
                    </div>
                  )}

                  {/* Submissions */}
                  <div>
                    <div className="font-black text-slate-900 flex items-center gap-2 mb-3 text-sm">
                      <FileText size={14} className="text-slate-500" /> Submissions ({customerSubs.length})
                    </div>
                    {customerSubs.length === 0 && <div className="text-xs text-slate-400 py-2">No submissions yet</div>}
                    <div className="space-y-2">
                      {customerSubs.map((s) => (
                        <Link key={s.id} to={`/admin/submissions?q=${encodeURIComponent(s.reference)}`}
                          className="block border border-slate-200 rounded-xl px-3 py-2 bg-white hover:border-indigo-300 hover:shadow-sm transition">
                          <div className="flex items-center justify-between gap-2">
                            <code className="text-[11px] font-mono font-black text-sky-600">{s.reference}</code>
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${STATUS_COLORS[s.status] || "bg-slate-100 text-slate-600"}`}>
                              {(s.status || "—").toUpperCase()}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-700 mt-1 font-bold">{s.brand_name} · ${Number(s.card_amount || 0).toFixed(0)}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-between">
                            <span>{fmtRelative(s.created_at)}</span>
                            <span className="font-mono text-emerald-700 font-black">${Number(s.estimated_payout || 0).toFixed(2)}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{scrollbar-width:none}`}</style>
    </AdminLayout>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-black w-16 shrink-0">{label}</span>
      <span className={`text-xs text-slate-900 truncate ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
